/**
 * Practice-ready gate: pitch games require a custom calibrated chanter
 * that has been verified once in Chanter Test after the latest calibration.
 *
 * Keyboard / Standard are blocked in production. Keyboard is allowed in
 * development for owner testing only.
 */

import { getItem, setItem } from './storage';
import { getGlobalChanter, getGlobalChanterId, isUsingStandardChanter } from './globalSettings';
import { STANDARD_CHANTER_UUID, KEYBOARD_CHANTER_UUID } from './constants';
import { trackEvent } from './analytics';

export const PRACTICE_RETURN_TO_KEY = 'practice_return_to';
export const PRACTICE_VERIFICATIONS_KEY = 'practice_chanter_verifications';

/** Minimum distinct note frequencies to treat a chanter as calibrated */
export const MIN_CALIBRATED_NOTES = 5;

/**
 * Stable fingerprint of a frequency map so edits invalidate verification.
 * @param {Record<string, number>|null|undefined} frequencies
 * @returns {string}
 */
export function getCalibrationFingerprint(frequencies) {
  if (!frequencies || typeof frequencies !== 'object') return '';
  const entries = Object.keys(frequencies)
    .sort()
    .map(key => `${key}:${Math.round(Number(frequencies[key]) || 0)}`);
  return entries.join('|');
}

/**
 * @returns {Array<{id: string, name: string, frequencies?: object}>}
 */
export function getSavedChanters() {
  return getItem('bagpipe_chanters', []) || [];
}

/**
 * @param {string} chanterId
 * @returns {object|null}
 */
export function getSavedChanterById(chanterId) {
  if (!chanterId) return null;
  return getSavedChanters().find(c => c.id === chanterId) || null;
}

/**
 * @param {object|null} chanter
 * @returns {boolean}
 */
export function hasCalibrationFrequencies(chanter) {
  if (!chanter || !chanter.frequencies) return false;
  const count = Object.values(chanter.frequencies).filter(
    hz => typeof hz === 'number' && hz > 0
  ).length;
  return count >= MIN_CALIBRATED_NOTES;
}

/**
 * @returns {Record<string, string>} chanterId → fingerprint
 */
function getVerificationMap() {
  const map = getItem(PRACTICE_VERIFICATIONS_KEY, {});
  return map && typeof map === 'object' ? map : {};
}

/**
 * Clear verification for a chanter (call after calibration save / freq change).
 * @param {string} chanterId
 */
export function invalidateChanterVerification(chanterId) {
  if (!chanterId || chanterId === STANDARD_CHANTER_UUID || chanterId === KEYBOARD_CHANTER_UUID) {
    return;
  }
  const map = { ...getVerificationMap() };
  if (map[chanterId]) {
    delete map[chanterId];
    setItem(PRACTICE_VERIFICATIONS_KEY, map);
    trackEvent('practice_verification_invalidated', {
      category: 'engagement',
      chanter_id: chanterId,
    });
  }
}

/**
 * Mark current calibration as verified in Chanter Test.
 * @param {string} chanterId
 * @param {Record<string, number>} frequencies
 */
export function markChanterVerified(chanterId, frequencies) {
  if (!chanterId || !hasCalibrationFrequencies({ frequencies })) return false;
  const fingerprint = getCalibrationFingerprint(frequencies);
  const map = { ...getVerificationMap(), [chanterId]: fingerprint };
  setItem(PRACTICE_VERIFICATIONS_KEY, map);
  trackEvent('practice_chanter_verified', {
    category: 'engagement',
    chanter_id: chanterId,
  });
  return true;
}

/**
 * Catalog / digital practice chanters ship with known frequencies.
 * @param {object|null|undefined} chanter
 * @returns {boolean}
 */
export function isDigitalPracticeChanter(chanter) {
  return Boolean(chanter && (chanter.isDigital || chanter.digitalInfo));
}

/**
 * Digital (and other fully-frequency) profiles are practice-ready without Chanter Test.
 * Auto-marks verification so Manage Chanters / gate stay in sync.
 * @param {object|null|undefined} chanter
 * @returns {boolean} true if ready for pitch games
 */
export function ensureChanterPracticeReady(chanter) {
  if (!chanter?.id || !hasCalibrationFrequencies(chanter)) return false;
  if (isChanterVerified(chanter.id, chanter.frequencies)) return true;
  // Digital presets: known freqs — skip Chanter Test. Also soft-migrate older
  // saved profiles that already have a full frequency map.
  return markChanterVerified(chanter.id, chanter.frequencies);
}

/**
 * @param {string} chanterId
 * @param {Record<string, number>} frequencies
 * @returns {boolean}
 */
export function isChanterVerified(chanterId, frequencies) {
  if (!chanterId || !frequencies) return false;
  const map = getVerificationMap();
  const expected = getCalibrationFingerprint(frequencies);
  return Boolean(expected && map[chanterId] === expected);
}

/**
 * Keyboard allowed only while developing locally.
 * @returns {boolean}
 */
export function isDevKeyboardPracticeAllowed() {
  return process.env.NODE_ENV === 'development';
}

/**
 * @typedef {'ready'|'standard'|'keyboard_blocked'|'no_custom'|'uncalibrated'|'unverified'} PracticeGateReason
 */

/**
 * @returns {{
 *   ready: boolean,
 *   reason: PracticeGateReason,
 *   chanter: object|null,
 *   chanterName: string,
 *   chanterId: string,
 *   customChanters: object[],
 *   calibratedCustomChanters: object[],
 * }}
 */
export function getPracticeGateStatus() {
  const chanterId = getGlobalChanterId();
  const chanterName = getGlobalChanter() || 'Standard';
  const customChanters = getSavedChanters().filter(
    c => c.id !== STANDARD_CHANTER_UUID && c.id !== KEYBOARD_CHANTER_UUID
  );
  const calibratedCustomChanters = customChanters.filter(hasCalibrationFrequencies);

  if (chanterId === KEYBOARD_CHANTER_UUID || chanterName === 'Keyboard') {
    if (isDevKeyboardPracticeAllowed()) {
      return {
        ready: true,
        reason: 'ready',
        chanter: null,
        chanterName: 'Keyboard',
        chanterId,
        customChanters,
        calibratedCustomChanters,
      };
    }
    return {
      ready: false,
      reason: 'keyboard_blocked',
      chanter: null,
      chanterName: 'Keyboard',
      chanterId,
      customChanters,
      calibratedCustomChanters,
    };
  }

  if (
    isUsingStandardChanter() ||
    chanterId === STANDARD_CHANTER_UUID ||
    chanterName === 'Standard'
  ) {
    return {
      ready: false,
      reason: 'standard',
      chanter: null,
      chanterName: 'Standard',
      chanterId,
      customChanters,
      calibratedCustomChanters,
    };
  }

  const chanter = getSavedChanterById(chanterId);
  if (!chanter) {
    return {
      ready: false,
      reason: 'no_custom',
      chanter: null,
      chanterName,
      chanterId,
      customChanters,
      calibratedCustomChanters,
    };
  }

  if (!hasCalibrationFrequencies(chanter)) {
    return {
      ready: false,
      reason: 'uncalibrated',
      chanter,
      chanterName: chanter.name || chanterName,
      chanterId,
      customChanters,
      calibratedCustomChanters,
    };
  }

  // Digital presets (and any profile with a full frequency map) are ready —
  // Chanter Test is optional, not a hard gate.
  ensureChanterPracticeReady(chanter);

  return {
    ready: true,
    reason: 'ready',
    chanter,
    chanterName: chanter.name || chanterName,
    chanterId,
    customChanters,
    calibratedCustomChanters,
  };
}

export function isPracticeReady() {
  return getPracticeGateStatus().ready;
}

/**
 * @param {string} path e.g. '/blaster'
 */
export function setPracticeReturnTo(path) {
  if (!path || typeof path !== 'string') return;
  setItem(PRACTICE_RETURN_TO_KEY, path);
}

/**
 * @param {string} [fallback='/games']
 * @returns {string}
 */
export function consumePracticeReturnTo(fallback = '/games') {
  const target = getItem(PRACTICE_RETURN_TO_KEY, null);
  setItem(PRACTICE_RETURN_TO_KEY, null);
  if (typeof target === 'string' && target.startsWith('/')) {
    return target;
  }
  return fallback;
}

export function peekPracticeReturnTo() {
  const target = getItem(PRACTICE_RETURN_TO_KEY, null);
  return typeof target === 'string' && target.startsWith('/') ? target : null;
}
